import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  BedDouble,
  Bus,
  Footprints,
  Landmark,
  Leaf,
  MapPin,
  Rotate3D,
  Store,
  UtensilsCrossed,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useURLConfigContext } from '@/contexts/URLConfigContext';
import { api } from '@/lib/api';
import { KioskBottomBar } from '@/components/KioskBottomBar';

interface Poi {
  id: string;
  name: string;
  description: string | null;
  latitude: string;
  longitude: string;
  category: string;
  color: string;
  icon: string;
  imageUrl: string | null;
  virtualTourId: string | null;
  displayOrder: number;
}

export const POI_CATEGORIES = [
  { value: 'general', label: 'General', emoji: '📍' },
  { value: 'naturaleza', label: 'Naturaleza', emoji: '🌿' },
  { value: 'cultura', label: 'Cultura', emoji: '🏛️' },
  { value: 'gastronomia', label: 'Gastronomía', emoji: '🍽️' },
  { value: 'servicios', label: 'Servicios', emoji: '🏪' },
  { value: 'deporte', label: 'Deporte', emoji: '⛷️' },
  { value: 'alojamiento', label: 'Alojamiento', emoji: '🏨' },
  { value: 'transporte', label: 'Transporte', emoji: '🚌' },
] as const;

export type PoiCategory = typeof POI_CATEGORIES[number]['value'];

const VALDERREDIBLE_CENTER: L.LatLngExpression = [42.80572439864576, -3.9405850452100455];
const VALDERREDIBLE_ZOOM = 12;

const CATEGORY_ICONS: Record<string, LucideIcon> = {
  general: MapPin,
  naturaleza: Leaf,
  cultura: Landmark,
  gastronomia: UtensilsCrossed,
  servicios: Store,
  deporte: Footprints,
  alojamiento: BedDouble,
  transporte: Bus,
};

const CATEGORY_MARKER_SVG: Record<string, string> = {
  general: '<path d="M12 21s6-4.35 6-11a6 6 0 1 0-12 0c0 6.65 6 11 6 11Z"/><circle cx="12" cy="10" r="2"/>',
  naturaleza: '<path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 19 2 19 2c0 7-2.8 12-8 12"/><path d="M2 21c0-3 1.85-5.36 5.08-6.94C9.85 12.7 13.55 12 18 12"/>',
  cultura: '<path d="m3 10 9-6 9 6"/><path d="M5 10v8M9 10v8M15 10v8M19 10v8M3 21h18"/>',
  gastronomia: '<path d="M6 2v7a3 3 0 0 0 3 3V2M6 6h3M9 12v10M16 2v20M16 2c3 2 3 7 0 9"/>',
  servicios: '<path d="M3 9l2-6h14l2 6"/><path d="M5 13v8h14v-8M9 21v-6h6v6"/><path d="M3 9a3 3 0 0 0 6 0 3 3 0 0 0 6 0 3 3 0 0 0 6 0"/>',
  deporte: '<path d="m14 2 3 3-2 2-3-3 2-2ZM12 6l-3 4 4 2 2 4M9 10l-4 2M13 12l-4 5-5 3M15 16l4 4"/>',
  alojamiento: '<path d="M2 4v16M22 20V9a2 2 0 0 0-2-2H10v13M2 15h20M6 7h4"/>',
  transporte: '<path d="M6 17h12M6 17a2 2 0 1 1-4 0V7c0-3 2-5 5-5h10c3 0 5 2 5 5v10a2 2 0 1 1-4 0M2 11h20M7 21l-2 2M17 21l2 2"/><circle cx="7" cy="7" r="1"/><circle cx="17" cy="7" r="1"/>',
};

const markerSvg = (category: string) => (
  `<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${CATEGORY_MARKER_SVG[category] ?? CATEGORY_MARKER_SVG.general}</svg>`
);

const normalizePoi = (poi: Record<string, unknown>): Poi => ({
  id: String(poi.id),
  name: String(poi.name ?? ''),
  description: poi.description ? String(poi.description) : null,
  latitude: String(poi.latitude ?? poi.lat ?? '0'),
  longitude: String(poi.longitude ?? poi.lon ?? '0'),
  category: String(poi.category ?? 'general'),
  color: String(poi.color ?? '#24963F'),
  icon: String(poi.icon ?? '📍'),
  imageUrl: poi.imageUrl || poi.image_url ? String(poi.imageUrl ?? poi.image_url) : null,
  virtualTourId: poi.virtualTourId || poi.virtual_tour_id
    ? String(poi.virtualTourId ?? poi.virtual_tour_id)
    : null,
  displayOrder: Number(poi.displayOrder ?? poi.display_order ?? 0),
});

function createPoiIcon(poi: Poi, selected = false) {
  const size = selected ? 48 : 36;
  return L.divIcon({
    html: `<span class="map-poi-marker${selected ? ' is-selected' : ''}" style="--poi-color:${poi.color};width:${size}px;height:${size}px"><span>${markerSvg(poi.category)}</span></span>`,
    className: 'map-poi-marker-shell',
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
  });
}

const MapPage = () => {
  const navigate = useNavigate();
  const config = useURLConfigContext();
  const [pois, setPois] = useState<Poi[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPoi, setSelectedPoi] = useState<Poi | null>(null);
  const mapDivRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markersRef = useRef<Map<string, L.Marker>>(new Map());

  useEffect(() => {
    if (!mapDivRef.current || mapRef.current) return;

    const map = L.map(mapDivRef.current, {
      center: VALDERREDIBLE_CENTER,
      zoom: VALDERREDIBLE_ZOOM,
      zoomControl: false,
    });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap',
      maxZoom: 19,
    }).addTo(map);
    L.control.zoom({ position: 'topright' }).addTo(map);
    map.on('click', () => setSelectedPoi(null));
    mapRef.current = map;

    window.setTimeout(() => {
      map.invalidateSize();
      map.setView(VALDERREDIBLE_CENTER, VALDERREDIBLE_ZOOM, { animate: false });
    }, 80);

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!config.municipalityId) {
      setLoading(false);
      return;
    }

    api.pois.list(config.municipalityId)
      .then((data) => setPois((data ?? []).map((poi) => normalizePoi(poi))))
      .catch((error) => console.error('Error loading map points:', error))
      .finally(() => setLoading(false));
  }, [config.municipalityId]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current.clear();

    pois.forEach((poi) => {
      const latitude = Number(poi.latitude);
      const longitude = Number(poi.longitude);
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;

      const marker = L.marker([latitude, longitude], {
        icon: createPoiIcon(poi),
        riseOnHover: true,
        bubblingMouseEvents: false,
      })
        .addTo(map)
        .on('click', () => setSelectedPoi(poi));

      markersRef.current.set(poi.id, marker);
    });
  }, [pois]);

  useEffect(() => {
    markersRef.current.forEach((marker, id) => {
      const poi = pois.find((item) => item.id === id);
      if (poi) marker.setIcon(createPoiIcon(poi, selectedPoi?.id === id));
    });
  }, [pois, selectedPoi]);

  const goHome = () => {
    const params = new URLSearchParams(window.location.search);
    navigate({ pathname: '/', search: params.toString() });
  };

  const goBack = () => {
    const historyIndex = window.history.state?.idx;
    if (typeof historyIndex === 'number' && historyIndex > 0) navigate(-1);
    else goHome();
  };

  const openVirtualTour = (tourId: string) => {
    const params = new URLSearchParams(window.location.search);
    params.set('tour', tourId);
    navigate({ pathname: '/visitas-360', search: params.toString() });
  };

  const SelectedPoiIcon = selectedPoi
    ? CATEGORY_ICONS[selectedPoi.category] ?? MapPin
    : MapPin;

  return (
    <div className="kiosk-screen-shell map-screen-shell">
      <main className="map-screen kiosk-screen-content">
        <div ref={mapDivRef} className="interactive-map" aria-label="Mapa interactivo de Valderredible" />

        {loading && (
          <div className="map-loading-state">
            <div className="loading-spinner" />
            <span>Cargando puntos...</span>
          </div>
        )}

        {!loading && pois.length === 0 && (
          <div className="map-empty-state">No hay puntos de interés configurados.</div>
        )}

        {selectedPoi && (
          <article
            className={`map-poi-card ${selectedPoi.imageUrl ? 'has-image' : ''}`}
            style={{ '--poi-color': selectedPoi.color } as React.CSSProperties}
          >
            {selectedPoi.imageUrl && (
              <div
                className="map-poi-card-image"
                style={{ backgroundImage: `url(${JSON.stringify(selectedPoi.imageUrl)})` }}
                aria-hidden="true"
              />
            )}
            <div className="map-poi-card-copy">
              <div className="map-poi-card-heading">
                <span><SelectedPoiIcon aria-hidden="true" /></span>
                <h2>{selectedPoi.name}</h2>
              </div>
              {selectedPoi.description && <p>{selectedPoi.description}</p>}
              {selectedPoi.virtualTourId && (
                <button type="button" onClick={() => openVirtualTour(selectedPoi.virtualTourId!)}>
                  <Rotate3D /> Ver visita 360°
                </button>
              )}
            </div>
            <button
              type="button"
              className="map-poi-card-close"
              aria-label="Cerrar ficha"
              onClick={() => setSelectedPoi(null)}
            >
              <X />
            </button>
          </article>
        )}
      </main>
      <KioskBottomBar onBack={goBack} onHome={goHome} />
    </div>
  );
};

export default MapPage;
